<template>
  <v-container fluid class="switch-orders-details-vuetify">
    <v-row>
      <v-col cols="12">
        <h2>Search Operations</h2>
      </v-col>
    </v-row>

    <!-- Initialization Loading/Error States -->
    <v-row v-if="!isSearchReadyAndInitialized">
      <v-col cols="12">
        <v-alert v-if="initializationError" type="error" prominent>
          <strong>Error initializing search:</strong> {{ initializationError }}
          <p>Please check the console for more details.</p>
        </v-alert>
        <div v-else class="text-center">
          <v-progress-linear
            v-if="isInitializing && totalRecordsToProcess > 0"
            :value="(recordsProcessed / totalRecordsToProcess) * 100"
            color="primary" height="25" class="mb-2" stream rounded
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}% ({{ initializationStatus }})</strong>
            </template>
          </v-progress-linear>
          <v-progress-circular
            v-if="isInitializing && totalRecordsToProcess === 0"
            indeterminate color="primary" size="64" width="6" class="mb-2"
          ></v-progress-circular>
          <p v-if="isInitializing" class="mt-1">Please wait...</p>
          <p v-else class="mt-1">Search system preparing...</p>
        </div>
      </v-col>
    </v-row>

    <!-- Global Search Field -->
    <v-card v-if="isSearchReadyAndInitialized" class="mb-5 pa-3" outlined>
      <v-text-field
        v-model="searchTerm"
        label="Search all fields"
        @input="debouncedSearch"
        clearable
        outlined
        dense
        hide-details="auto"
      />
    </v-card>

    <!-- Search Results -->
    <div v-if="isSearchReadyAndInitialized">
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-progress-linear indeterminate color="primary" class="mb-3" />
          <p class="text-center">Searching...</p>
        </v-col>
      </v-row>
      <div v-else>
        <v-alert v-if="searchPerformed && searchResults.length === 0 && hasActiveFilters" type="info" outlined class="mb-3">
          No records found matching your query.
        </v-alert>
        <v-data-table @click:row="onRowClick"
          :headers="tableHeaders"
          :items="searchResults"
          :items-per-page="25"
          item-key="id"
          class="elevation-1"
          :loading="isLoading"
          loading-text="Loading data..."
          no-data-text="No data to display. Try clearing the search or performing a search."
          dense
        >
          <template v-slot:top>
            <v-toolbar flat dense color="transparent">
              <v-toolbar-title>
                Showing {{ searchResults.length }}
                <span v-if="hasActiveFilters"> of {{ totalPossibleResults }} matching records</span>
              </v-toolbar-title>
            </v-toolbar>
          </template>
        </v-data-table>
      </div>
    </div>
    <SwitchOrdersDetailsDialog :operationId="selectedOperationId" />
  </v-container>
</template>

<script>
import FlexSearch from 'flexsearch';
import pako from 'pako';
import SwitchOrdersDetailsDialog from './SwitchOrdersDetailsDialog.vue';

const GZIPPED_JSON_URL =
  'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/operations%2Fswitchorders%2Fdetails_data.json.gz?alt=media&token=227b2752-fea5-40f9-8935-dabb8ff6933f';

export default {
  name: 'SwitchOrdersDetails',
  components: {
    SwitchOrdersDetailsDialog
  },
  data() {
    return {
      searchTerm: '',
      allFetchedData: [],
      searchResults: [],
      isLoading: false,
      searchPerformed: false,
      debounceTimer: null,
      totalPossibleResults: 0,
      searchIndexInstance: null,
      isSearchReadyAndInitialized: false,
      isInitializing: false,
      initializationStatus: 'Starting...',
      initializationError: null,
      recordsProcessed: 0,
      totalRecordsToProcess: 0,
      tableHeaders: [
        { text: 'Prepared Date', value: 'prepared_date' },
        { text: 'Job ID', value: 'job_id' },
        { text: 'Device Cleaned', value: 'device_cleaned' },
        { text: 'Phase', value: 'phase' },
        { text: 'Location', value: 'location' },
        { text: 'Address', value: 'address' },
        { text: 'Operation Category', value: 'operation_category' },
        { text: 'Count', value: 'count' },
        { text: 'Sequence', value: 'sequence' },
        { text: 'Serial', value: 'serial' }
      ],
      selectedOperationId: ""
    };
  },
  computed: {
    hasActiveFilters() {
      return this.searchTerm.trim() !== '';
    }
  },
  methods: {
    onRowClick(item) {
      this.selectedOperationId = item.id;
    },
    async initializeSearchInternal() {
      if (this.searchIndexInstance) {
        this.showInitialData();
        return;
      }
      this.isInitializing = true;
      try {
        // Add a cache-busting query parameter instead of headers to avoid CORS issues
        const cacheBustUrl = `${GZIPPED_JSON_URL}&_cb=${Date.now()}`;
        
        // Use a simple fetch without custom headers to avoid CORS preflight
        const resp = await fetch(cacheBustUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = new Uint8Array(await resp.arrayBuffer());
        let text;
        if (buf[0] === 0x1f && buf[1] === 0x8b) {
          text = pako.ungzip(buf, { to: 'string' });
        } else {
          text = new TextDecoder('utf-8').decode(buf);
        }
        const data = JSON.parse(text);
        
        // Process data to extract and parse the prepared-date field
        data.forEach((record, index) => {
          // Debug: Log the first few records to see the structure
          if (index < 3) {
            console.log('Record structure:', record);
            console.log('Fields:', record.fields);
            if (record.fields) {
              console.log('Fields keys:', Object.keys(record.fields));
              console.log('prepared-date value:', record.fields['prepared-date']);
            }
          }
          
          if (record.fields && record.fields['prepared-date']) {
            // Parse date from format "Sat Feb 10, 2024 @ 03:07"
            const dateStr = record.fields['prepared-date'];
            const parsedDate = this.parseDate(dateStr);
            record.prepared_date = parsedDate;
            
            // Debug: Log successful parsing
            if (index < 3) {
              console.log('Original date:', dateStr);
              console.log('Parsed date:', parsedDate);
            }
          } else {
            record.prepared_date = '';
            
            // Debug: Log when field is missing
            if (index < 3) {
              console.log('No prepared-date field found in record', index);
            }
          }
        });
        
        this.allFetchedData = data;
        this.totalRecordsToProcess = data.length;
        const idx = new FlexSearch.Document({
          charset: 'latin:advanced', language: 'en',
          document: {
            id: 'id',
            index: ['prepared_date','job_id','device_cleaned','phase','location','address','operation_category','count','sequence','serial'],
            store: true
          }
        });
        const chunkSize = 10000;
        const fieldsToSanitize = ['prepared_date','job_id','device_cleaned','phase','location','address','operation_category','count','sequence','serial'];
        for (let i = 0; i < data.length; i += chunkSize) {
          data.slice(i, i + chunkSize).forEach(record => {
            fieldsToSanitize.forEach(field => {
              if (record[field] == null) record[field] = '';
              else if (typeof record[field] !== 'string') record[field] = String(record[field]);
            });
            idx.add(record);
          });
          this.recordsProcessed = Math.min(i + chunkSize, data.length);
          await new Promise(res => setTimeout(res, 0));
        }
        this.searchIndexInstance = idx;
        this.isSearchReadyAndInitialized = true;
        this.showInitialData();
      } catch (err) {
        console.error('[INIT] error', err);
        
        // Provide more helpful error message if it's a CORS error
        if (err.message && err.message.includes('CORS')) {
          this.initializationError = 'CORS Policy Error: Firebase Storage access is blocked in this environment. Try setting up CORS rules for your Firebase Storage bucket.';
          console.error('This is a CORS error. Make sure your Firebase Storage CORS settings include the domain you are deploying to.');
        } else {
          this.initializationError = err.message;
        }
      } finally {
        this.isInitializing = false;
      }
    },
    showInitialData() {
      this.searchResults = [...this.allFetchedData];
      this.totalPossibleResults = this.allFetchedData.length;
      this.searchPerformed = false;
      this.isLoading = false;
    },
    async performSearch() {
      if (!this.searchIndexInstance) return;
      this.isLoading = true;
      this.searchPerformed = true;

      const rawTerm = this.searchTerm.trim();
      if (!rawTerm) {
        this.showInitialData();
        this.isLoading = false;
        return;
      }

      // Split on commas for multi-term
      const terms = rawTerm.toLowerCase().split(',').map(t => t.trim()).filter(t => t);
      const fields = ['prepared_date','job_id','device_cleaned','phase','location','address','operation_category','count','sequence','serial'];
      let results = [];

      if (terms.length === 1) {
        // Single-term: use FlexSearch
        let raw = [];
        try {
          raw = await this.searchIndexInstance.searchAsync({
            query: terms[0],
            enrich: true,
            limit: 10000
          });
        } catch (e) {
          console.error('[SEARCH] FlexSearch error:', e);
        }
        // Flatten
        const items = [];
        raw.forEach(set => set.result ? items.push(...set.result) : items.push(set));
        // Extract and dedupe
        const seen = new Set();
        items.forEach(it => {
          const r = it.doc || it;
          if (!seen.has(r.id)) {
            seen.add(r.id);
            results.push(r);
          }
        });
      } else {
        // Multi-term: manual filter across all fields
        results = this.allFetchedData.filter(rec =>
          terms.every(term =>
            fields.some(field =>
              String(rec[field] || '').toLowerCase().includes(term)
            )
          )
        );
      }

      this.searchResults = results;
      this.totalPossibleResults = results.length;
      this.isLoading = false;
    },
    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.performSearch(), 400);
    },
    parseDate(dateStr) {
      if (!dateStr) {
        console.log('parseDate: empty dateStr');
        return '';
      }
      
      console.log('parseDate: processing', dateStr);
      
      try {
        // Parse format "Sat Feb 10, 2024 @ 03:07"
        // Remove the day of week and "@" symbol for easier parsing
        const cleanedStr = dateStr.replace(/^[A-Za-z]{3}\s/, '').replace(' @ ', ' ');
        console.log('parseDate: cleaned string', cleanedStr);
        
        // Parse the date
        const date = new Date(cleanedStr);
        console.log('parseDate: parsed date object', date);
        
        if (isNaN(date.getTime())) {
          // If parsing fails, return the original string
          console.log('parseDate: parsing failed, returning original');
          return dateStr;
        }
        
        // Format as YYYY-MM-DD for better display and sorting
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        const formatted = `${year}-${month}-${day}`;
        console.log('parseDate: formatted result', formatted);
        return formatted;
      } catch (error) {
        console.warn('Date parsing error:', error, 'for:', dateStr);
        return dateStr; // Return original if parsing fails
      }
    },
    clearFiltersAndShowInitial() {
      this.searchTerm = '';
      this.showInitialData();
    }
  },
  created() {
    this.initializeSearchInternal();
  }
};
</script>

<style scoped>
.switch-orders-details-vuetify { padding-top: 16px; }
.v-data-table { margin-top: 16px; }
</style>
